import MarkdownIt from "markdown-it";
import type { RouteLocationRaw } from "vue-router";

import type { AskReference, AskVersion } from "@/lib/openapi/gen/types.gen";

/**
 * Last-resort guard against a leaked ask envelope reaching the reader.
 *
 * `parse_ask_output` on the backend already falls back to prose whenever it
 * cannot find an envelope, so most of the time this does nothing. It exists for
 * the one shape that falls through on purpose: an object carrying only an
 * `answer`. The backend's discriminator refuses to treat that as an envelope —
 * too weak a signal, since any JSON an answer happens to quote has an `answer`
 * key as readily as a real envelope does — and so returns it verbatim, which
 * would render as `{"answer": "…"}` in the box.
 *
 * **Only unwraps when the object is the entire reply.** That restriction is the
 * whole safety of this function. Scanning for an envelope *inside* prose would
 * mean an answer that quotes `{"answer": "your text here"}` — explaining this
 * very format, say — gets replaced wholesale by that fragment, turning a
 * complete answer into four words. Prose is the thing being protected here, so
 * a guard that can eat it is worse than no guard at all.
 *
 * The fence is handled because the prompt asks for "a single JSON code block",
 * so a leak arrives wrapped in one more often than not.
 *
 * Not `stripAgentEnvelope`: that keys on `message`, the thread agent's
 * envelope, and would never match this one.
 *
 * Never throws, and returns the content untouched for anything it is unsure of.
 */
const isAskEnvelope = (data: unknown): data is { answer: string } =>
    data !== null &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    typeof (data as Record<string, unknown>).answer === "string";

/** Strip one wrapping ```…``` fence, if the whole string is inside it. */
const unfence = (text: string): string => {
    if (!text.startsWith("```")) return text;
    const lines = text.split("\n");
    if (lines.length < 2 || lines[lines.length - 1].trim() !== "```")
        return text;
    return lines.slice(1, -1).join("\n").trim();
};

export const stripAskEnvelope = (content: string): string => {
    if (!content) return content;

    const candidate = unfence(content.trim());
    if (!candidate.startsWith("{") || !candidate.endsWith("}")) return content;

    let parsed: unknown;
    try {
        parsed = JSON.parse(candidate);
    } catch {
        return content;
    }
    return isAskEnvelope(parsed) ? parsed.answer : content;
};

// html:false keeps raw HTML escaped, so rendering the model's prose is XSS-safe
// without a sanitizer — the same contract every other markdown site in this app
// relies on.
const md = new MarkdownIt({ html: false, linkify: true, breaks: true });

/**
 * One answer's prose as HTML, ready to hand to `v-html`.
 *
 * Stripped before rendering, not after: markdown-it would turn a leaked
 * envelope's braces and quotes into escaped HTML entities, and there would be
 * no JSON left to recognise.
 *
 * Here rather than in a component because there are now two places an answer is
 * drawn — the live one and every earlier one — and the escaping contract above
 * is the sort of thing that gets half-copied.
 */
export const renderAnswer = (content: string): string =>
    md.render(stripAskEnvelope(content || ""));

/** How a reference is told apart: the type and id together, never the name. */
export const referenceKey = (reference: {
    entity_type: string;
    entity_id: string;
}): string => `${reference.entity_type}:${reference.entity_id}`;

export type LinkReferencesOptions = {
    /** Where a reference's own page is — or nothing when it has none. */
    locate: (reference: AskReference) => RouteLocationRaw | null | undefined;
    /** The `href` to put on the anchor, for a location that exists. */
    href: (to: RouteLocationRaw) => string;
    /**
     * An icon class to put before the name, so the reader sees what kind of
     * thing it is — a process, a task — without leaving the sentence. Absent,
     * the anchor is the bare name.
     */
    icon?: (reference: AskReference) => string | undefined;
    /** A `title` for the anchor: the kind of thing, spelled out on hover. */
    title?: (reference: AskReference) => string | undefined;
};

export type LinkedAnswer = {
    html: string;
    /** `referenceKey`s of every reference that became at least one link. */
    linked: Set<string>;
};

const escapeForRegExp = (text: string): string =>
    text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Whole-word: not touching a letter or digit on either side. */
const wholeWord = (name: string): RegExp =>
    new RegExp(
        `(^|[^\\p{L}\\p{N}])(${escapeForRegExp(name)})(?![\\p{L}\\p{N}])`,
        "gu"
    );

/**
 * Turn the names in a rendered answer into links to the things they name.
 *
 * The references already carry everything a link needs — type, id, name — and
 * every id has been through the stek gate before it got here. So the answer's
 * own words can be the links, and the chip row underneath only has to carry
 * what the prose did not name. One way to read, one place to click, nothing
 * said twice.
 *
 * What it refuses to link, and why each refusal exists:
 *
 * - **A name two references share.** "Check" in the Accountant lane and "Check"
 *   in the Controller lane are one word in the text and two things in the
 *   model; a link would have to pick. Neither is linked, and both stay as
 *   chips — the row can hold two things with one name, the prose cannot.
 * - **A reference with no page.** `locate` says where a thing lives or that it
 *   lives nowhere; a link to nowhere looks authoritative and is worse than none.
 * - **Text already inside a link, or inside code.** A name in a URL or a code
 *   span is not a mention of the thing.
 * - **Part of a word.** "Check" must not be found inside "Check invoice" —
 *   longer names are claimed first, and once claimed they are inside an anchor
 *   and out of reach; what is left is matched on word boundaries.
 *
 * Works on the rendered HTML through a real parser rather than on the markdown
 * or with a regex over tags: matching happens on text nodes, so a name with
 * `&` or `<` in it needs no escaping care, and no tag can be split.
 *
 * `locate` and `href` are injected so this stays pure. The component that
 * calls it owns the router and the thread-revision wrapping; this owns only
 * the text.
 */
export const linkReferences = (
    html: string,
    references: AskReference[],
    options: LinkReferencesOptions
): LinkedAnswer => {
    const linked = new Set<string>();

    const byName = new Map<string, Map<string, AskReference>>();
    for (const reference of references) {
        const name = (reference.entity_name ?? "").trim();
        if (name.length < 2) continue;
        const keys = byName.get(name) ?? new Map<string, AskReference>();
        keys.set(referenceKey(reference), reference);
        byName.set(name, keys);
    }

    const candidates: {
        name: string;
        reference: AskReference;
        to: RouteLocationRaw;
    }[] = [];
    for (const [name, keys] of byName) {
        if (keys.size !== 1) continue;
        const reference = [...keys.values()][0];
        const to = options.locate(reference);
        if (to) candidates.push({ name, reference, to });
    }
    candidates.sort((a, b) => b.name.length - a.name.length);

    if (!candidates.length || !html) {
        return { html, linked };
    }

    const doc = new DOMParser().parseFromString(
        `<div id="ask-answer-root">${html}</div>`,
        "text/html"
    );
    const root = doc.getElementById("ask-answer-root");
    if (!root) {
        return { html, linked };
    }

    const textNodes = (): Text[] => {
        const found: Text[] = [];
        const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        for (let node = walker.nextNode(); node; node = walker.nextNode()) {
            const parent = (node as Text).parentElement;
            if (parent && !parent.closest("a, code, pre")) {
                found.push(node as Text);
            }
        }
        return found;
    };

    for (const { name, reference, to } of candidates) {
        const pattern = wholeWord(name);
        const key = referenceKey(reference);
        const href = options.href(to);
        const icon = options.icon?.(reference);
        const title = options.title?.(reference);

        for (const node of textNodes()) {
            const text = node.data;
            pattern.lastIndex = 0;
            if (!pattern.test(text)) continue;
            pattern.lastIndex = 0;

            const fragment = doc.createDocumentFragment();
            let cursor = 0;
            for (
                let match = pattern.exec(text);
                match;
                match = pattern.exec(text)
            ) {
                const [, lead, found] = match;
                const start = match.index + lead.length;
                fragment.append(text.slice(cursor, start));
                const anchor = doc.createElement("a");
                anchor.className = "ask-answer__link";
                anchor.href = href;
                anchor.dataset.entityType = reference.entity_type;
                anchor.dataset.entityId = reference.entity_id;
                if (title) anchor.title = title;
                if (icon) {
                    const glyph = doc.createElement("i");
                    glyph.className = icon;
                    glyph.setAttribute("aria-hidden", "true");
                    anchor.append(glyph);
                }
                anchor.append(found);
                fragment.append(anchor);
                cursor = start + found.length;
                linked.add(key);
            }
            fragment.append(text.slice(cursor));
            node.replaceWith(fragment);
        }
    }

    return { html: root.innerHTML, linked };
};

/**
 * The label beside a question: which version of the business its answer
 * described. One word for a draft — the title made the label long enough to
 * compete with the question, and it belongs on hover (`versionDetail`).
 *
 * `null` for an answer from before labels existed, and the caller shows
 * nothing for that: a missing label must never be read as "the shared
 * version".
 */
export const versionNote = (
    version: AskVersion | null | undefined
): string | null => {
    if (!version) return null;
    return version.kind === "draft" ? "Draft" : "Shared version";
};

/**
 * What the label says on hover: the draft by its title, worded the way the
 * draft bar words it. Recorded by the server when the answer was written and
 * never recomputed, so it is the draft's title *as it was then* — a draft
 * renamed or deleted since keeps its old name here.
 */
export const versionDetail = (
    version: AskVersion | null | undefined
): string | null => {
    if (!version) return null;
    if (version.kind !== "draft") return "The version everyone sees";
    const title = version.draft_title?.trim();
    return title ? `Draft · ${title}` : "Draft";
};
